// src/app/services/auth.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap, map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private jwtHelper = new JwtHelperService();

  /** Base URL preferindo api.baseUrl, senão backendUrl */
  private static readonly BASE_URL: string =
    (environment as any)?.api?.baseUrl ??
    (environment as any)?.backendUrl ??
    '';

  /** Sufixos do backend desta aplicação */
  private static readonly AUTH_LOGIN = `${AuthService.BASE_URL}/api/auth/login`;
  private static readonly AUTH_LOGOUT = `${AuthService.BASE_URL}/api/auth/logout`;
  private static readonly AUTH_VERSION = `${AuthService.BASE_URL}/api/auth/version`;

  /** Storage keys (mantidas do serviço Old) */
  public static readonly ACCESS_TOKEN_KEY = '@acelerador_avatax:access_token';
  public static readonly REFRESH_TOKEN_KEY = '@acelerador_avatax:refresh_token';
  public static readonly USUARIO_LOGIN_KEY = 'usuarioLogin';

  /** ---------- Fluxo de autenticação ---------- */

  /** Login no endpoint desta aplicação. Retorna JSON com token e nome. */
  signIn(login: any): Observable<any> {
    return this.http
      .post<any>(AuthService.AUTH_LOGIN, {
        username: login.username,
        password: login.password,
      })
      .pipe(
        tap((response) => {
          if (response?.token) {
            this.initializeSessionLogin(response.token, response.nome ?? '');
          }
        })
      );
  }

  /** Logout no endpoint desta aplicação. */
  logout(): Observable<any> {
    return this.http.post<any>(AuthService.AUTH_LOGOUT, {}).pipe(
      tap(() => {
        this.destroySession();
        this.router.navigate(['/login']);
      })
    );
  }

  /** Versão do backend */
  getVersion(): Observable<string> {
    return this.http.get<string>(AuthService.AUTH_VERSION);
  }

  /** ---------- Token & sessão ---------- */

  initializeSession(accessToken: string) {
    sessionStorage.setItem(AuthService.ACCESS_TOKEN_KEY, accessToken);
  }

  initializeSessionLogin(accessToken: string, loginUsuario: string) {
    sessionStorage.setItem(AuthService.ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.setItem(AuthService.USUARIO_LOGIN_KEY, loginUsuario);
  }

  destroySession(): void {
    sessionStorage.removeItem(AuthService.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(AuthService.USUARIO_LOGIN_KEY);
    sessionStorage.removeItem(AuthService.REFRESH_TOKEN_KEY);
  }

  setUsuarioFromToken(token: string) {
    const decoded: any = this.jwtHelper.decodeToken(token);
    if (decoded?.usuario) {
      sessionStorage.setItem(AuthService.USUARIO_LOGIN_KEY, decoded.usuario);
    }
  }

  obterUsuarioLogado(): string | null {
    return sessionStorage.getItem(AuthService.USUARIO_LOGIN_KEY);
  }

  getToken(): string | null {
    return sessionStorage.getItem(AuthService.ACCESS_TOKEN_KEY);
  }

  /** Cabeçalhos com Bearer */
  getAuthHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders(token ? { Authorization: `Bearer ${token}` } : {});
  }

  /** ---------- Helpers de JWT ---------- */

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && !this.jwtHelper.isTokenExpired(token);
  }

  isFirstAccess(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const decoded: any = this.jwtHelper.decodeToken(token);
    return !!decoded?.user_first_access;
    // ajuste a claim conforme seu backend
  }

  obterAmbienteLogado(): string {
    const token = this.getToken();
    if (!token) return '';
    const decoded: any = this.jwtHelper.decodeToken(token);
    return decoded?.ambiente ?? '';
  }

  obterGruposUsuario(): string[] {
    const token = this.getToken();
    if (!token) return [];
    const decoded: any = this.jwtHelper.decodeToken(token);
    // no Old você lia em "sub"; ajuste se o claim correto for outro
    return Array.isArray(decoded?.sub) ? decoded.sub : [];
  }
}
